// Kanban board layout for WorkflowPage — desktop only (md+)

import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
} from '@chakra-ui/react';
import { TaskCardV2, TaskCardStatus } from '../shared/TaskCardV2';
import type { WebTaskItem } from '@/lib/sdk/workflows/types';

export interface KanbanTask {
  taskItem: WebTaskItem;
  status: TaskCardStatus;
  index: number; // original list position for task numbering
}

export interface KanbanBoardProps {
  tasks: KanbanTask[];
  onTaskClick: (task: WebTaskItem) => void;
}

const KANBAN_COLUMNS: {
  title: string;
  statuses: TaskCardStatus[];
  colorScheme: string;
  headerColor: string;
}[] = [
  {
    title: 'Pending',
    statuses: ['pending', 'due_soon', 'soft_overdue', 'blocked'],
    colorScheme: 'gray',
    headerColor: 'text-secondary',
  },
  {
    title: 'In Progress',
    statuses: ['wip'],
    colorScheme: 'orange',
    headerColor: 'orange.500',
  },
  {
    title: 'Completed',
    statuses: ['completed'],
    colorScheme: 'green',
    headerColor: 'green.500',
  },
];

export function KanbanBoard({ tasks, onTaskClick }: KanbanBoardProps) {
  return (
    <HStack spacing={4} align="start" w="full">
      {KANBAN_COLUMNS.map((col) => {
        const colTasks = tasks.filter((t) => col.statuses.includes(t.status));

        return (
          <Box key={col.title} flex={1}>
            {/* Column header: label left, count badge right */}
            <HStack
              justify="space-between"
              align="center"
              mb={3}
              pb={2}
              borderBottomWidth="2px"
              borderColor={col.headerColor}
            >
              <Text
                fontWeight="semibold"
                fontSize="sm"
                color={col.headerColor}
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {col.title}
              </Text>
              <Badge colorScheme={col.colorScheme} fontSize="sm">{colTasks.length}</Badge>
            </HStack>

            {/* Column body */}
            <VStack
              spacing={3}
              align="stretch"
              overflowY="auto"
              maxH="65vh"
              sx={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'var(--chakra-colors-gray-400)',
                  borderRadius: '2px',
                },
              }}
            >
              {colTasks.map(({ taskItem, status, index }) => (
                <TaskCardV2
                  key={taskItem.task.id}
                  title={taskItem.task.task_name}
                  number={index + 1}
                  status={status}
                  displayValue={taskItem.display_value}
                  softDueDate={taskItem.task.soft_due_date}
                  userName={taskItem.task.user_name}
                  updatedAt={taskItem.task.updated_at}
                  notes={taskItem.task.notes}
                  description={taskItem.task.description}
                  documentLinks={taskItem.task.document_links ?? []}
                  tags={taskItem.task.tags ?? []}
                  onClick={() => onTaskClick(taskItem)}
                />
              ))}
            </VStack>
          </Box>
        );
      })}
    </HStack>
  );
}
